import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Link, Navigate, useLocation } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { useCurrentUser } from "@/features/auth";
import { useApiErrorMessage } from "@/shared/api";
import { BlockError } from "@/shared/ui/block-error";
import { Button } from "@/shared/ui/button";
import { Skeleton } from "@/shared/ui/skeleton";

import { useHighlightsAllowed } from "../../api/useHighlightsAllowed";
import { useHighlightsOverview } from "../../api/useHighlightsOverview";
import { useRequestClips } from "../../api/useRequestClips";
import { filterMatches } from "../../model/filterMatches";
import type { HighlightView } from "../../model/Highlight";
import type { HighlightsSearch } from "../../model/highlightsSearchSchema";
import { ClipDialog } from "../ClipDialog";
import { HighlightsFilters } from "./HighlightsFilters";
import { MatchHighlights } from "./MatchHighlights";

const HEADING = msg`Highlights`;
const LEAD = msg`We find the best moments in your matches ourselves. A clip is recorded on request, usually within 30 minutes.`;
const EMPTY = msg`No highlights yet`;
const EMPTY_TEXT = msg`Every hour we look through your fresh matches. Multikills, big ultimates and parry streaks will show up here.`;
const TO_MATCHES = msg`To matches`;
const NOTHING = msg`Nothing matches the filter`;
const RESET = msg`Show everything`;
const LOAD_ERROR = msg`Could not load your highlights`;

// Записи истории, на которых раздел уже прокрутил к матчу: «Назад» на ту же запись не
// прокручивает снова, а оставляет место, которое восстановил роутер.
const scrolledEntries = new Set<string>();

type HighlightsPageProps = {
  search: HighlightsSearch;
  onSearchChange: (search: HighlightsSearch) => void;
};

// Раздел «Хайлайты» по макету design/prototype/HighlightsByMatch.dc.html (18д): матчи сверху
// новые, в каждом — исход, K/D/A, переход в разбор, полоса матча и все моменты, готовые и без
// клипа. Фильтр и матч, к которому пришли из разбора или списка, — в search params. Хайлайты
// закрыты для игрока — уводим на главную.
export const HighlightsPage = ({ search, onSearchChange }: HighlightsPageProps) => {
  const { i18n } = useLingui();
  const overview = useHighlightsOverview(search.match);
  const requestClips = useRequestClips();
  const requestError = useApiErrorMessage(requestClips.error);
  const [openId, setOpenId] = useState<number | null>(null);
  const { data: user } = useCurrentUser();
  const allowed = useHighlightsAllowed();
  const entry = useLocation({ select: (location) => location.state.__TSR_key ?? location.href });

  // Пришли к матчу из разбора или списка матчей — прокручиваем к нему, когда он появился.
  // Один раз на запись истории: при «Назад» роутер сам вернёт прокрутку.
  const targetFound =
    search.match !== undefined &&
    overview.data?.matches.some((match) => match.match_id === search.match) === true;
  // Фильтр меняет запись истории (replace даёт новый ключ): её тоже помечаем, а внутри одного
  // захода к тому же матчу второй раз не прокручиваем.
  const scrolledMatch = useRef<number | null>(null);
  useEffect(() => {
    if (!targetFound || search.match === undefined) {
      return;
    }
    const seen = scrolledEntries.has(entry) || scrolledMatch.current === search.match;
    scrolledEntries.add(entry);
    scrolledMatch.current = search.match;
    if (!seen) {
      document.getElementById(`match-${search.match}`)?.scrollIntoView({ block: "start" });
    }
  }, [targetFound, search.match, entry]);

  if (user !== undefined && !allowed) {
    return <Navigate to="/" replace />;
  }

  const open = (highlight: HighlightView) => {
    setOpenId(highlight.id);
  };

  const body = () => {
    if (overview.isPending) {
      return [0, 1].map((key) => <Skeleton key={key} className="h-[340px] rounded-[14px]" />);
    }
    if (overview.isError) {
      return (
        <BlockError
          title={i18n._(LOAD_ERROR)}
          retrying={overview.isFetching}
          onRetry={() => void overview.refetch()}
        />
      );
    }
    const all = filterMatches(overview.data.matches, {});
    if (all.length === 0) {
      return (
        <section className="flex flex-col items-start gap-2 rounded-[14px] border border-dashed border-border bg-surface p-6">
          <span className="text-lg font-semibold">{i18n._(EMPTY)}</span>
          <span className="text-muted">{i18n._(EMPTY_TEXT)}</span>
          <Button asChild variant="outline" className="mt-2">
            <Link to="/">{i18n._(TO_MATCHES)}</Link>
          </Button>
        </section>
      );
    }
    const matches = filterMatches(overview.data.matches, search);
    return (
      <>
        <HighlightsFilters
          matches={all}
          filters={{ show: search.show, hero: search.hero }}
          onChange={(filters) => {
            onSearchChange({ ...search, ...filters });
          }}
        />
        {requestError !== null && (
          <p role="alert" className="text-sm text-bad">
            {requestError}
          </p>
        )}
        {matches.length === 0 ? (
          <div className="flex flex-wrap items-center gap-3 py-3 text-subtle">
            {i18n._(NOTHING)}
            <Button
              variant="outline"
              onClick={() => {
                onSearchChange({ match: search.match, from: search.from });
              }}
            >
              {i18n._(RESET)}
            </Button>
          </div>
        ) : (
          matches.map((match) => (
            <MatchHighlights
              key={match.match_id}
              match={match}
              clipsEnabled={overview.data.clips_enabled}
              requestClips={requestClips}
              target={match.match_id === search.match}
              fromAnalysis={match.match_id === search.match && search.from === "analysis"}
              onOpen={open}
            />
          ))
        )}
      </>
    );
  };

  // Плеер ищет клип и его матч в полном ответе: фильтр мог спрятать матч, пока клип открыт.
  const openMatch = overview.data?.matches.find((match) =>
    match.items.some((highlight) => highlight.id === openId),
  );
  const openHighlight = openMatch?.items.find((highlight) => highlight.id === openId) ?? null;
  return (
    <main
      data-page="highlights"
      className="mx-auto flex w-full max-w-[1088px] flex-col gap-5 px-6 pt-7 pb-16 max-sm:px-4"
    >
      <div className="flex flex-col gap-1">
        <h1 className="font-display text-[34px] leading-tight">{i18n._(HEADING)}</h1>
        <span className="text-muted">{i18n._(LEAD)}</span>
      </div>
      {body()}
      <ClipDialog
        highlight={openHighlight}
        match={openMatch}
        onOpen={open}
        onClose={() => {
          setOpenId(null);
        }}
      />
    </main>
  );
};
